import { v4 as uuidv4 } from 'uuid';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { Editor as TinyMCEReactEditor } from '@tinymce/tinymce-react';
import { DOCUMENT_UPLOAD_STATUS_ID, PresignedUploadModel } from '@/lib/models';
import { uploadFile } from '@/lib/utils';

interface TinyMCEProps {
	blogToolbar?: boolean;
	initialValue?: string;
	presignedUploadGetter?(fileInfo: {
		fileId: string;
		fileName: string;
		fileType: string;
		fileSize: number;
	}): Promise<{ upload: PresignedUploadModel }>;
	uploadStatusUpdater?(upload: PresignedUploadModel, uploadStatusId: DOCUMENT_UPLOAD_STATUS_ID): Promise<void>;
	disabled?: boolean;
}

export interface TinyMCERef {
	getContent: () => string;
	setContent: (content: string) => void;
}

export const TinyMCE = React.forwardRef(function TinyMCE(
	{ blogToolbar, initialValue, presignedUploadGetter, uploadStatusUpdater, disabled }: TinyMCEProps,
	ref: React.Ref<TinyMCERef> | undefined
) {
	const editorRef = useRef<TinyMCEEditor | null>(null);
	const [editorKey, setEditorKey] = useState(0);

	useEffect(() => {
		setEditorKey((previousKey) => previousKey + 1);
	}, [blogToolbar]);

	useImperativeHandle(
		ref,
		() => ({
			getContent: () => {
				if (!editorRef.current) {
					return '';
				}

				return editorRef.current.getContent();
			},
			setContent: (content) => {
				if (!editorRef.current) {
					return;
				}

				editorRef.current.setContent(content);
			},
		}),
		[]
	);

	return (
		<TinyMCEReactEditor
			key={editorKey}
			onInit={(_event, editor) => (editorRef.current = editor)}
			initialValue={initialValue}
			licenseKey="gpl"
			tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
			init={{
				height: blogToolbar ? 800 : 200,
				menubar: false,
				convert_urls: false,
				relative_urls: false,
				remove_script_host: false,
				color_map: [
					'#FFFFFF',
					'White',
					'#DEE2E6',
					'Light Gray',
					'#ADB5BD',
					'Medium Gray',
					'#343A40',
					'Dark Gray',
					'#000000',
					'Black',
					'#006FBA',
					'Blue',
					'#F47920',
					'Orange',
					'#454595',
					'Purple',
					'#7FAA3A',
					'Green',
					'#F8AF0A',
					'Yellow',
					'#CC3333',
					'Red',
					'#00A1DE',
					'Info',
					'#73C9F9',
					'Light Azure',
					'#4B99F7',
					'Sky Blue',
					'#235CE8',
					'Blue',
					'#184389',
					'Dark Blue',
					'#002F5F',
					'Dark',
				],
				sandbox_iframes: true,
				sandbox_iframes_exclusions: ['muse.ai', 'poweredbyhackett.com', 'spendmatters-site.s3.amazonaws.com'],
				plugins: [
					// 'accordion',
					'anchor',
					'autolink',
					// 'autoresize',
					// 'autosave',
					'charmap',
					// 'code',
					// 'codesample',
					// 'directionality',
					'emoticons',
					// 'fullscreen',
					// 'help',
					'image',
					// 'importcss',
					// 'insertdatetime',
					'link',
					'lists',
					// 'advlist',
					'media',
					// 'nonbreaking',
					// 'pagebreak',
					// 'preview',
					// 'quickbars',
					// 'save',
					// 'searchreplace',
					'table',
					// 'visualblocks',
					// 'visualchars',
					// 'wordcount',
				],
				paste_data_images: false,
				smart_paste: false,
				toolbar: blogToolbar
					? 'blocks | bold italic underline strikethrough forecolor backcolor | link image media | numlist bullist outdent indent | undo redo | hr table'
					: 'blocks | bold italic underline strikethrough forecolor backcolor | link | numlist bullist outdent indent | undo redo | hr',
				resize: true,
				...(blogToolbar
					? {
							image_uploadtab: true,
							images_upload_handler: async (blobInfo, progressFn) => {
								try {
									if (!presignedUploadGetter || !uploadStatusUpdater) {
										throw new Error('File upload is not configured.');
									}

									const response = await uploadFile({
										file: new File([blobInfo.blob()], blobInfo.filename()),
										presignedUploadGetter: () =>
											presignedUploadGetter({
												fileId: uuidv4(),
												fileName: blobInfo.filename(),
												fileType: blobInfo.blob().type,
												fileSize: blobInfo.blob().size,
											}),
										uploadStatusUpdater,
										onProgress: (percentage) => {
											progressFn(percentage);
										},
									});

									return response.accessUrl;
								} catch (error) {
									console.error('Image upload failed:', error);
									throw error;
								}
							},
							content_style: `
								img { max-width: 100%; height: auto; }
								p { margin: 0; padding: 0; }
								ol { margin-bottom: 0; }
								ul { margin-bottom: 0}
							`,
					  }
					: { block_formats: 'Heading 2=h2; Heading 3=h3; Normal=p' }),
			}}
			disabled={disabled}
		/>
	);
});
